import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import MapManager from '../../../map/MapManager';
import 'ol/ol.css';
import { useLayout } from '../../../hooks/screen/useLayout';
import { MainHead } from '../../../components/common/head/MainHead';
import { useAppSelector, useAppDispatch } from '../../../hooks/store/useStore';
import { managedFarmSlice } from '../../../store/managedFarmSlice';
import { AddressSearchModal, SearchAddressItemForFieldMap } from './components/AddressSearchModal';
import { FieldInfoSlideModal } from './components/FieldInfoSlideModal';
import { getLandAreaInfo } from '../../../api/vworld/getLandCharacteristics';
import { managedFarmPageInfo } from '../../../constants/pageInfo/pageInfo.constant';
import { useNavigate } from 'react-router-dom';
import { ampli } from '../../../ampli';
import { getAddressLevelsFromVWorld } from '../../../api/vworld/getAddressLevelsFromVWorld';

export const ManagedFarmFieldMapPage = () => {
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [isShowAddressSearchModal, setIsShowAddressSearchModal] = useState(false);

  // 지적도 모달 상태
  const [isLandInfoModalOpen, setIsLandInfoModalOpen] = useState<boolean>(false);
  const [selectedLandInfo, setSelectedLandInfo] = useState<{
    address: string;
    addressTrailingParts: string;
    landArea: number;
    areaInPyeong: number;
    pnu?: string;
  } | null>(null);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const managedFarmState = useAppSelector((state) => state.managedFarm);
  const { address, coordinates: storedCoordinates, pnu: storedPnu } = managedFarmState;
  const { setAddress, setLandInfo, setCoordinates, setPnu, setMapImage } = managedFarmSlice.actions;

  // 지도 준비 상태 관리
  const [isMapReady, setIsMapReady] = useState<boolean>(false);

  const formatAddressTitle = async (address: string): Promise<string> => {
    if (!address) {
      return '';
    }

    // 원본 주소를 마지막 3자리로 자르는 함수
    const formatOriginalAddress = (addr: string): string => {
      const parts = addr.split(' ').filter((part) => part.trim());
      return parts.slice(-3).join(' ');
    };

    try {
      const addressLevels = await getAddressLevelsFromVWorld(address);

      if (!addressLevels) {
        console.warn('⚠️ 주소 레벨 정보를 가져올 수 없습니다:', address);
        return formatOriginalAddress(address); // 원본 주소 마지막 3자리 반환으로 폴백
      }

      // 우선순위에 따라 주소 요소를 배열에 담습니다.
      const potentialParts = [
        addressLevels.level2,
        addressLevels.level3,
        addressLevels.level4,
        addressLevels.level4L,
        addressLevels.level5,
      ];

      // 비어있지 않은 (truthy) 요소만 필터링하고, 각 요소를 공백으로 분할하여 개별 단위로 만듭니다.
      const allParts = potentialParts
        .filter((part) => part && typeof part === 'string')
        .flatMap((part) => part.split(/\s+/).filter((p) => p.trim()))
        .filter((part) => part.length > 0);

      // 마지막 3개의 요소를 가져옵니다.
      const titleParts = allParts.slice(-3);

      // 결과가 비어있으면 원본 주소 마지막 3자리 반환
      const result = titleParts.join(' ');
      return result || formatOriginalAddress(address);
    } catch (error) {
      console.error('❌ 주소 포맷팅 실패:', error);
      return formatOriginalAddress(address); // 에러 시 원본 주소 마지막 3자리 반환으로 폴백
    }
  };

  const closeLandInfoModal = useCallback(() => {
    setIsLandInfoModalOpen(false);
  }, []);

  const handleRegisterWorkField = useCallback(async () => {
    if (selectedLandInfo && selectedLandInfo.pnu) {
      const mapImage = await MapManager.captureFramedArea(310, 175, 1);

      dispatch(setMapImage(mapImage || ''));

      ampli.pvmClickFarmTaskRegisterButton({
        field_address: selectedLandInfo.address,
        area_in_pyeong: selectedLandInfo.areaInPyeong.toString(),
      });

      const addressTrailingParts = await formatAddressTitle(selectedLandInfo.address);

      dispatch(
        setLandInfo({
          landArea: selectedLandInfo.landArea,
          addressTrailingParts,
          areaInPyeong: selectedLandInfo.areaInPyeong,
          pnu: selectedLandInfo.pnu,
        }),
      );

      setIsLandInfoModalOpen(false);
      navigate(managedFarmPageInfo.formFieldOwnerPage.path, { replace: true });
    } else {
      console.error('❌ 선택된 필지 정보가 없습니다.');
    }
  }, [selectedLandInfo, dispatch, navigate]);

  // 지도 초기화
  useEffect(() => {
    const timer = setTimeout(() => {
      if (mapContainerRef.current) {
        const map = MapManager.initialize('map');

        setTimeout(() => {
          map.updateSize();
          // 지도가 완전히 렌더링된 후 준비 상태 설정
          setTimeout(() => {
            setIsMapReady(true);
          }, 100);
        }, 100);
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      MapManager.cleanup();
      setIsMapReady(false);
    };
  }, []);

  // 검색 입력창 클릭 시 주소 검색 모달 표시
  const handleSearchInputClick = useCallback(() => {
    setIsShowAddressSearchModal(true);
  }, []);

  // VWorld 주소 선택 완료 처리
  const handleVWorldAddressSelected = useCallback(
    (item: SearchAddressItemForFieldMap) => {
      setIsShowAddressSearchModal(false);
      setIsLandInfoModalOpen(false);

      // Redux store에 선택된 주소와 좌표, PNU 저장
      dispatch(setAddress(item.address));
      dispatch(setCoordinates({ x: item.coordinates.x, y: item.coordinates.y }));
      dispatch(setPnu(item.pnu));
      setSearchKeyword(item.address);

      // 지적도 직접 로드
      if (item.coordinates && item.pnu && isMapReady) {
        const loadDirectCadastral = async () => {
          try {
            const displayResult = await MapManager.loadAndDisplayCadastralLayer(
              item.pnu,
              item.coordinates.x,
              item.coordinates.y,
            );

            if (displayResult.success) {
              // 토지특성조회 API 호출
              try {
                const landAreaInfo = await getLandAreaInfo(item.pnu);
                const addressTrailingParts = await formatAddressTitle(item.address);

                setSelectedLandInfo({
                  address: item.address,
                  landArea: landAreaInfo?.landArea || 0,
                  areaInPyeong: landAreaInfo?.areaInPyeong || 0,
                  addressTrailingParts,
                  pnu: item.pnu,
                });

                // 모달 표시
                setTimeout(() => {
                  setIsLandInfoModalOpen(true);
                }, 250);
              } catch (error) {
                console.error('❌ 토지특성조회 실패:', error);
                const addressTrailingParts = await formatAddressTitle(item.address);
                setSelectedLandInfo({
                  address: item.address,
                  landArea: 0,
                  areaInPyeong: 0,
                  addressTrailingParts,
                  pnu: item.pnu,
                });
                setTimeout(() => {
                  setIsLandInfoModalOpen(true);
                }, 250);
              }
            } else {
              console.error('❌ 지적도 로드 실패:', displayResult.error);
              setLocationError(displayResult.error || '지적도 로드 실패');
            }
          } catch (error) {
            console.error('❌ VWorld 지적도 로드 에러:', error);
            setLocationError('지적도 로드 중 오류가 발생했습니다.');
          }
        };

        loadDirectCadastral();
      }
    },
    [dispatch, isMapReady],
  );

  // Redux store에서 선택된 주소로 지적도 직접 표시
  useEffect(() => {
    if (address && isMapReady) {
      // 검색 키워드 업데이트
      if (searchKeyword !== address) {
        setSearchKeyword(address);
      }

      // 좌표와 PNU가 있으면 바로 지적도 표시
      if (storedCoordinates && storedCoordinates.x && storedCoordinates.y && storedPnu) {
        const timer = setTimeout(async () => {
          try {
            // 지적도 직접 로드 (MapManager 내부에서 레이어 관리)
            const displayResult = await MapManager.loadAndDisplayCadastralLayer(
              storedPnu,
              storedCoordinates.x,
              storedCoordinates.y,
            );

            if (displayResult.success) {
              // 토지특성조회 API 호출
              try {
                const landAreaInfo = await getLandAreaInfo(storedPnu);
                const addressTrailingParts = await formatAddressTitle(address);

                setSelectedLandInfo({
                  address,
                  landArea: landAreaInfo?.landArea || 0,
                  areaInPyeong: landAreaInfo?.areaInPyeong || 0,
                  addressTrailingParts,
                  pnu: storedPnu,
                });

                // 모달 표시
                setTimeout(() => {
                  setIsLandInfoModalOpen(true);
                }, 250);
              } catch (error) {
                console.error('❌ 토지특성조회 실패:', error);
                const addressTrailingParts = await formatAddressTitle(address);
                setSelectedLandInfo({
                  address,
                  landArea: 0,
                  areaInPyeong: 0,
                  pnu: storedPnu,
                  addressTrailingParts,
                });
                setTimeout(() => {
                  setIsLandInfoModalOpen(true);
                }, 250);
              }
            } else {
              console.error('❌ 지적도 로드 실패:', displayResult.error);
              setLocationError(displayResult.error || '지적도 로드 실패');
            }
          } catch (error) {
            console.error('❌ 지적도 로드 에러:', error);
            setLocationError('지적도 로드 중 오류가 발생했습니다.');
          }
        }, 200);

        return () => clearTimeout(timer);
      }
    }
    return undefined;
  }, [address, isMapReady, storedCoordinates, storedPnu, searchKeyword]);

  useLayout({
    fixedHeader: (
      <MainHead
        backButton={{ isShow: true }}
        searchInput={{
          searchKeyword,
          setSearchKeyword,
          inputRef: searchInputRef,
          searchInputType: 'managedFarm',
          onSearch: handleSearchInputClick,
        }}
      />
    ),
    fixedFooter: <></>,
    fixedHeaderHeight: 48,
    fixedFooterHeight: 0,
    shouldShowFooter: false,
  });

  return (
    <>
      <Container>
        <MapWrapper>
          <MapContainer ref={mapContainerRef} id="map" />
          {locationError && <ErrorMessage>{locationError}</ErrorMessage>}
        </MapWrapper>
      </Container>

      {/* VWorld 주소 검색 모달 */}
      <AddressSearchModal
        isShow={isShowAddressSearchModal}
        onClose={() => setIsShowAddressSearchModal(false)}
        onAddressSelected={handleVWorldAddressSelected}
      />

      {/* 지적도 정보 모달 */}
      <FieldInfoSlideModal
        isShow={isLandInfoModalOpen}
        landInfo={selectedLandInfo}
        onRegister={handleRegisterWorkField}
        onClose={closeLandInfoModal}
      />
    </>
  );
};

const Container = styled.div`
  width: 100%;
  height: 100vh;
  position: relative;
  display: flex;
  flex-direction: column;
`;

const MapWrapper = styled.div`
  flex: 1;
  position: relative;
`;

const MapContainer = styled.div`
  width: 100%;
  height: 100%;
`;

const ErrorMessage = styled.div`
  position: absolute;
  top: 20px;
  left: 20px;
  right: 20px;
  background: rgba(255, 0, 0, 0.8);
  color: white;
  padding: 12px;
  border-radius: 8px;
  font-size: 14px;
  z-index: 1000;
`;
